// 🧠 STORE SIMPLE PARA GESTIÓN DE ESTADO OFFLINE
// Gestiona el estado de conexión y gastos pendientes

import { OfflineExpense, getPendingExpenses, markExpenseAsSynced, removeExpenseFromQueue, incrementRetryCount } from './offlineQueue';

class OfflineStore {
  private listeners: Set<() => void> = new Set();
  private syncInterval: NodeJS.Timeout | null = null;
  private isSyncing = false;

  // Estado
  private _hasConnection = navigator.onLine;
  private _pendingExpenses: OfflineExpense[] = [];

  constructor() {
    // Cargar gastos pendientes al iniciar
    this.loadPendingExpenses();
    
    // Escuchar cambios de conexión
    window.addEventListener('online', () => {
      this._hasConnection = true;
      this.notifyListeners();
      console.log('🌐 Conexión restaurada');
      // Intentar sincronizar inmediatamente
      this.syncExpenses();
    });
    
    window.addEventListener('offline', () => {
      this._hasConnection = false;
      this.notifyListeners();
      console.log('📴 Conexión perdida');
    });
  }

  /**
   * Suscribirse a cambios del store
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notificar a todos los listeners
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  /**
   * Cargar gastos pendientes desde localStorage
   */
  loadPendingExpenses(): void {
    this._pendingExpenses = getPendingExpenses();
    this.notifyListeners();
  }

  /**
   * Obtener gastos pendientes
   */
  get pendingExpenses(): OfflineExpense[] {
    return [...this._pendingExpenses];
  }

  /**
   * Obtener estado de conexión
   */
  get hasConnection(): boolean {
    return this._hasConnection;
  }

  /**
   * Agregar gasto offline (ya guardado en localStorage por offlineQueue)
   */
  addExpenseOffline(): void {
    this.loadPendingExpenses();
  }

  /**
   * Sincronizar gastos pendientes con Supabase
   */
  async syncExpenses(): Promise<void> {
    // Evitar múltiples sincronizaciones simultáneas
    if (this.isSyncing) {
      console.log('⏳ Sincronización ya en curso, omitiendo...');
      return;
    }

    // Solo sincronizar si hay conexión
    if (!this._hasConnection) {
      console.log('📴 Sin conexión, no se puede sincronizar');
      return;
    }

    const pending = getPendingExpenses();
    if (pending.length === 0) {
      return;
    }

    this.isSyncing = true;
    console.log(`🔄 Iniciando sincronización de ${pending.length} gasto(s) pendiente(s)...`);

    try {
      // Importar dinámicamente para evitar dependencias circulares
      const { createGasto } = await import('./mockApi');
      
      // Sincronizar cada gasto pendiente
      for (const expense of pending) {
        try {
          // Preparar datos del gasto (sin imagenBase64 para el envío)
          const { imagenBase64, imagenFileName, ...gastoData } = expense.data;
          
          // Intentar sincronizar con timeout de 10 segundos
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Timeout: La operación tardó demasiado')), 10000)
          );

          const result = await Promise.race([
            createGasto(gastoData),
            timeoutPromise
          ]) as any;

          if (result.success && result.data) {
            // Sincronización exitosa
            markExpenseAsSynced(expense.idTemp);
            removeExpenseFromQueue(expense.idTemp);
            console.log(`✅ Gasto sincronizado: ${expense.idTemp}`);
          } else {
            // Error al sincronizar, incrementar contador de reintentos
            incrementRetryCount(expense.idTemp);
            console.warn(`⚠️ Error al sincronizar gasto ${expense.idTemp}:`, result.message);
          }
        } catch (error) {
          // Error al sincronizar este gasto específico
          incrementRetryCount(expense.idTemp);
          console.error(`❌ Error al sincronizar gasto ${expense.idTemp}:`, error);
          // Continuar con el siguiente gasto
        }
      }

      // Recargar gastos pendientes después de sincronizar
      this.loadPendingExpenses();
    } catch (error) {
      console.error('❌ Error general en sincronización:', error);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Iniciar sincronización automática periódica
   */
  startAutoSync(intervalMs: number = 5000): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    this.syncInterval = setInterval(() => {
      this.syncExpenses();
    }, intervalMs);

    console.log(`🔄 Sincronización automática iniciada (cada ${intervalMs}ms)`);
  }

  /**
   * Detener sincronización automática
   */
  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      console.log('⏹️ Sincronización automática detenida');
    }
  }
}

// Exportar instancia singleton
export const offlineStore = new OfflineStore();

